import { useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useLiveDataForMatch } from "@/hooks/useLiveData";
import { useMatchData } from "@/hooks/useMatchData";
import { useFilterOdds } from "@/hooks/useFilterOdds";
import { useMatchOdds } from "@/hooks/useMatchOdds";
import { useMatches } from "@/hooks/useMatches";
import { LIVE_STATUSES, NOT_STARTED_STATUS } from "@/contants";
import { formatUnixToVNDate } from "@/utils/format";
import {
  getOddValue,
  formatOddsByBookmarkerExtend,
  getOddByCompanyId,
} from "@/utils/matchInfo";
import MatchMinute from "@/components/KbvMatchMinute";
import ViuGoTipsAiModal from "@/components/ViuGoTipsAiModal";
import { IconTip, IconAnalyze } from "./icons";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";

function trendClass(trend) {
  if (trend === "increased") return " up";
  if (trend === "decreased") return " down";
  return "";
}

function trendArrow(trend) {
  if (trend === "increased") return <span className="odd-arrow">▲</span>;
  if (trend === "decreased") return <span className="odd-arrow">▼</span>;
  return null;
}

function OddHdp({ value, odds, trend, showGoal }) {
  return (
    <div className="odds-bet-box">
      <div className="odds-bet-trigger">
        <div className={`odd-hdp${trendClass(trend)}`}>
          {showGoal && value ? <span className="odd-goal">{value}</span> : null}
          <span className="odd-value">
            {odds || "-"} {trendArrow(trend)}
          </span>
        </div>
      </div>
    </div>
  );
}

function OddOu({ left, odds, trend }) {
  return (
    <div className="odds-bet-box">
      <div className="odds-bet-trigger">
        <div className={`odd-ou${trendClass(trend)}`}>
          {left != null && left !== "" ? (
            <span className="odd-left">{left}</span>
          ) : null}
          <span className="odd-value">
            {odds || "-"} {trendArrow(trend)}
          </span>
        </div>
      </div>
    </div>
  );
}

function OddBox({ odds, trend }) {
  return (
    <div className="odds-bet-box">
      <div className="odds-bet-trigger">
        <div className={`odd_box${trendClass(trend)}`}>
          <span className="odd-value">
            {odds || "-"} {trendArrow(trend)}
          </span>
        </div>
      </div>
    </div>
  );
}

function OddCustom({ title, odds, trend }) {
  return (
    <div className="odds-bet-box">
      <div className="odds-bet-trigger">
        <div className={`odd-custom${trendClass(trend)}`}>
          <span className="odd-title">{title}</span>
          <span className={trend === "increased" ? "up" : ""}>
            <span className="odd-value">{odds || "-"}</span>
          </span>
        </div>
      </div>
    </div>
  );
}

function pickCorner(match, cornerBucket, companyIdOdds, bookmarkerExtend) {
  const allBookmarker = Number(companyIdOdds) === -1;
  const liveList = cornerBucket?.live;
  const preList = cornerBucket?.pre;
  const useFirstResult = allBookmarker;
  const item =
    (Array.isArray(liveList) && liveList.length
      ? getOddByCompanyId(useFirstResult, liveList, companyIdOdds)
      : null) ||
    (Array.isArray(preList) && preList.length
      ? getOddByCompanyId(useFirstResult, preList, companyIdOdds)
      : null) ||
    getOddByCompanyId(useFirstResult, match?.totalCornersOdds, companyIdOdds);
  if (!item) {
    return {
      line: null,
      over: null,
      under: null,
      overTrend: "unchanged",
      underTrend: "unchanged",
    };
  }
  const line = getOddValue(item.instantHandicap ?? item.totalCorners, null);
  const over = formatOddsByBookmarkerExtend(
    getOddValue(item.instantOver ?? item.over, null),
    bookmarkerExtend || "hongkong",
  );
  const under = formatOddsByBookmarkerExtend(
    getOddValue(item.instantUnder ?? item.under, null),
    bookmarkerExtend || "hongkong",
  );
  return {
    line,
    over,
    under,
    overTrend:
      item.initialOverTrend && item.initialOverTrend !== "unchanged"
        ? item.initialOverTrend
        : item.initialOverTrendOld || "unchanged",
    underTrend:
      item.initialUnderTrend && item.initialUnderTrend !== "unchanged"
        ? item.initialUnderTrend
        : item.initialUnderTrendOld || "unchanged",
  };
}

export default function SportMatchRow({ match }) {
  const [openTipsAiModal, setOpenTipsAiModal] = useState(false);
  const [tipsAiInsightData, setTipsAiInsightData] = useState(null);
  const [loadingTipsAiInsight, setLoadingTipsAiInsight] = useState(false);
  const { getMatchInsight } = useMatches();

  const { getOdds, getScore } = useLiveDataForMatch(match?.id);
  const liveOdds = getOdds();
  const liveScore = getScore();

  const companyIdOdds = useSelector((state) => state.bookmakerReducer);
  const bookmarkerExtend = useSelector(
    (state) => state.bookmarkerExtendReducer,
  );
  const filterOdds = useFilterOdds(match, liveOdds, null, companyIdOdds);

  const {
    currentHomeScore,
    currentAwayScore,
    homeYellow,
    homeRed,
    awayYellow,
    awayRed,
    currentHomeCorner,
    currentAwayCorner,
  } = useMatchData(liveScore, match);

  const {
    handicapValue,
    homeHandicapOdds,
    awayHandicapOdds,
    homeHandicapTrend,
    awayHandicapTrend,
    overUnderValue,
    overOdds,
    underOdds,
    overTrend,
    underTrend,
    homeEuropeOdds,
    awayEuropeOdds,
    instantDrawOdds,
    homeEuropeTrend,
    awayEuropeTrend,
    drawTrend,
    handicapHalfValue,
    homeHandicapHalfOdds,
    awayHandicapHalfOdds,
  } = useMatchOdds(match, filterOdds, companyIdOdds);

  const corner = pickCorner(
    match,
    filterOdds.filterOtherOddsData?.totalCornersOdds,
    companyIdOdds,
    bookmarkerExtend,
  );

  const statusNum =
    liveScore?.status !== undefined
      ? Number(liveScore.status)
      : Number(match?.status ?? 0);
  const isLive = LIVE_STATUSES.includes(statusNum);
  const isNotStarted = NOT_STARTED_STATUS.includes(statusNum);
  const showScore = !isNotStarted;

  const { date: matchDateStr, gio: matchTimeStr } = formatUnixToVNDate(
    match?.matchTime,
  );
  const datePrefix =
    match?.matchTime != null
      ? dayjs.unix(match.matchTime).tz(TZ).format("DD/MM")
      : "";

  const homeName = match?.homeTeam?.nameVi || match?.homeName;
  const awayName = match?.awayTeam?.nameVi || match?.awayName;
  const homeLogo = match?.homeTeam?.logo || match?.homeLogo;
  const awayLogo = match?.awayTeam?.logo || match?.awayLogo;
  const leagueName =
    match?.league?.name || match?.leagueName || match?.league?.league_name;
  const leagueLogo =
    match?.league?.logo ||
    match?.leagueLogo ||
    match?.league_logo ||
    match?.league?.league_logo;

  const homeScoreDisp = showScore ? currentHomeScore ?? 0 : null;
  const awayScoreDisp = showScore ? currentAwayScore ?? 0 : null;
  const rawHomeCorner = liveScore?.homeCorner ?? match?.homeCorner;
  const rawAwayCorner = liveScore?.awayCorner ?? match?.awayCorner;
  const hasCorner =
    (rawHomeCorner != null && rawHomeCorner !== "") ||
    (rawAwayCorner != null && rawAwayCorner !== "");

  const handleOpenTipsAiModal = async (e) => {
    e?.stopPropagation?.();
    setOpenTipsAiModal(true);
    if (tipsAiInsightData || loadingTipsAiInsight || !match?.id) return;
    setLoadingTipsAiInsight(true);
    try {
      const insightResponse = await getMatchInsight(match.id);
      if (insightResponse?.success && insightResponse?.data) {
        setTipsAiInsightData(insightResponse.data);
      }
    } finally {
      setLoadingTipsAiInsight(false);
    }
  };

  const scoreNode =
    homeScoreDisp != null ? (
      <>
        <span>{homeScoreDisp}</span>
        <span>-</span>
        <span>{awayScoreDisp}</span>
      </>
    ) : (
      <span>-</span>
    );

  const cornerNode =
    !isNotStarted && (hasCorner || isLive) ? (
      <>
        <span>{rawHomeCorner ?? currentHomeCorner ?? 0}</span>
        <span>-</span>
        <span>{rawAwayCorner ?? currentAwayCorner ?? 0}</span>
      </>
    ) : (
      <span>-</span>
    );

  const desktopOdds = (
    <div className="td-cell odd-cell odd-two-line">
      <div className="odd-rows">
        <OddHdp odds={homeHandicapOdds} trend={homeHandicapTrend} />
        <OddHdp
          value={handicapValue}
          odds={awayHandicapOdds}
          trend={awayHandicapTrend}
          showGoal
        />
      </div>
      <div className="odd-rows">
        <OddOu left={overUnderValue} odds={overOdds} trend={overTrend} />
        <OddOu left="U" odds={underOdds} trend={underTrend} />
      </div>
      <div className="odd-rows">
        <OddOu left={corner.line} odds={corner.over} trend={corner.overTrend} />
        <OddOu odds={corner.under} trend={corner.underTrend} />
      </div>
      <div className="odd-rows">
        <OddBox odds={homeEuropeOdds} trend={homeEuropeTrend} />
        <OddBox odds={instantDrawOdds} trend={drawTrend} />
        <OddBox odds={awayEuropeOdds} trend={awayEuropeTrend} />
      </div>
    </div>
  );

  const mobileOdds = (
    <div className="td-cell odd-cell odd-cell-mb">
      <div className="odd-cols">
        <div className="odd-label">Cược chấp</div>
        <div className="odd-group">
          <OddHdp odds={homeHandicapOdds} trend={homeHandicapTrend} />
          <OddHdp
            value={handicapValue}
            odds={awayHandicapOdds}
            trend={awayHandicapTrend}
            showGoal
          />
        </div>
      </div>
      <div className="odd-cols">
        <div className="odd-label">Tài/Xỉu</div>
        <div className="odd-group">
          <OddOu left={overUnderValue} odds={overOdds} trend={overTrend} />
          <OddOu left="U" odds={underOdds} trend={underTrend} />
        </div>
      </div>
      <div className="odd-cols">
        <div className="odd-label">1x2</div>
        <div className="odd-1x2">
          <OddCustom
            title="Chủ"
            odds={homeEuropeOdds}
            trend={homeEuropeTrend}
          />
          <OddCustom
            title="Khách"
            odds={awayEuropeOdds}
            trend={awayEuropeTrend}
          />
          <OddCustom title="Hòa" odds={instantDrawOdds} trend={drawTrend} />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="row-table row-two-line">
        <div
          className={`td-cell m-time match-status${isLive ? " live" : ""}`}
        >
          <div className="m-time-inner">
            {isLive ? (
              <MatchMinute
                liveScore={liveScore}
                status={statusNum}
                matchTime={match?.matchTime}
                halfStartTime={match?.halfStartTime}
              />
            ) : (
              <span className="m-time-text">
                <span className="match-date-prefix">{datePrefix}</span>{" "}
                {matchTimeStr || "--"}
              </span>
            )}
            {isLive ? (
              <div className="live_icon_wrapper">
                <div className="m-live-mb">
                  <span>Live</span>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="td-cell m-score">{scoreNode}</div>

        <div
          className={`td-cell m-corner${
            isNotStarted || (!hasCorner && !isLive) ? " m-corner-no-start" : ""
          }`}
        >
          {cornerNode}
        </div>

        <div className="td-cell m-team">
          <div className="m-team-group">
            <div className="m-team-logo">
              {homeLogo ? (
                <img src={homeLogo} alt={homeName || ""} loading="lazy" />
              ) : null}
            </div>
            <div className="m-team-name">{homeName}</div>
          </div>
          <div className="m-team-score">{scoreNode}</div>
          <div className="m-team-group">
            <div className="m-team-logo">
              {awayLogo ? (
                <img src={awayLogo} alt={awayName || ""} loading="lazy" />
              ) : null}
            </div>
            <div className="m-team-name">{awayName}</div>
          </div>
        </div>

        <div className="td-cell m-link">
          <span
            className="m-link-icon"
            role="button"
            tabIndex={0}
            title="Tips AI"
            onClick={handleOpenTipsAiModal}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleOpenTipsAiModal(e);
              }
            }}
          >
            <IconTip width={14} height={14} />
          </span>
          <span
            className="m-link-icon"
            role="button"
            tabIndex={0}
            title="Tips AI"
            onClick={handleOpenTipsAiModal}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleOpenTipsAiModal(e);
              }
            }}
          >
            <IconAnalyze width={14} height={14} />
          </span>
        </div>

        {desktopOdds}
        {mobileOdds}
      </div>

      {openTipsAiModal &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed inset-0 z-[99999998] bg-black/50"
            style={{ backdropFilter: "blur(4px)" }}
            onClick={() => setOpenTipsAiModal(false)}
            aria-hidden="true"
          />,
          document.body,
        )}

      <ViuGoTipsAiModal
        open={openTipsAiModal}
        onClose={() => setOpenTipsAiModal(false)}
        match={match}
        matchInfo={{
          ...formatUnixToVNDate(match?.matchTime),
          isLive,
        }}
        statsInfo={{
          currentHomeScore: homeScoreDisp ?? 0,
          currentAwayScore: awayScoreDisp ?? 0,
          homeYellow,
          homeRed,
          awayYellow,
          awayRed,
          currentHomeCorner,
          currentAwayCorner,
        }}
        oddsInfo={{
          handicapValue,
          homeHandicapOdds,
          awayHandicapOdds,
          overUnderValue,
          overOdds,
          underOdds,
          homeEuropeOdds,
          awayEuropeOdds,
          instantDrawOdds,
          handicapHalfValue,
          homeHandicapHalfOdds,
          awayHandicapHalfOdds,
        }}
        leagueName={leagueName}
        leagueLogo={leagueLogo || ""}
        homeName={homeName}
        homeLogo={homeLogo || ""}
        awayName={awayName}
        awayLogo={awayLogo || ""}
        matchDate={matchDateStr}
        matchTime={matchTimeStr}
        matchTimestamp={match?.matchTime}
        headerScore={
          showScore
            ? `${homeScoreDisp ?? 0} - ${awayScoreDisp ?? 0}`
            : "-"
        }
        liveScoreToday={liveScore}
        matchStatusNum={statusNum}
        matchTimeUnix={match?.matchTime}
        halfStartTime={match?.halfStartTime}
        isMatchLive={isLive}
        statusLabel={isNotStarted ? "Sắp diễn ra" : isLive ? "Trực tiếp" : undefined}
        insightData={tipsAiInsightData}
        insightLoading={loadingTipsAiInsight}
      />
    </>
  );
}
